/* PULL APPROVED SKUs INTO A PROJECT FROM ANOTHER ONE — Tina / Jay / Max, 2026-10-12.

   Tina: "they want to split some SKU to have another project number ... should we need to get approval
   from YL again?" Jay: "implement 'SKU migration function' from 1 project to another for only those
   approved ones." Max, on where it lives: in Project Tracker's New Project and Edit forms, beside
   "+ Add SKU" — the project is created or edited the usual way, and approved SKUs of another project of
   the SAME team are picked here and moved in when the form is saved. They keep their SKU ID, their
   approval, their samples and stages; tickets already raised stay where they were raised.

   Three pieces, used by both forms so they behave alike:
     MoveSkusButton  the button beside "+ Add SKU" — offered to an admin or a manager of a Sales OP team,
                     live only once the form's team is one the viewer manages;
     MoveSkusPicker  choose the project to take them from, then tick its approved SKUs;
     MoveInList      what will move in on Save, each removable.
   And moveSkusInto(), which the save handlers call once their own save has landed: one request per
   source project, so each source's owner is told about their own project. */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft } from 'lucide-react';
import './MoveSkusIn.css';

export const mayMoveSkus = (me, teamKey) => !!me && (me.isAdmin || (!!teamKey && (me.managerTeams || []).includes(teamKey)));
const offeredAtAll = (me) => !!me && (me.isAdmin || (me.managerTeams || []).length > 0);

export function MoveSkusButton({ me, teamKey, onOpen }) {
  const { t } = useTranslation();
  if (!offeredAtAll(me)) return null;
  const why = !teamKey ? t('st.mvNeedTeam') : (!mayMoveSkus(me, teamKey) ? t('st.mvNotManager') : '');
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm mv-open"
      disabled={!!why}
      title={why || t('st.mvBtnTitle')}
      onClick={onOpen}
    >
      <ArrowRightLeft size={13} /> {t('st.mvBtn')}
    </button>
  );
}

/* ONE SEARCH, NOT A LIST TO SCROLL — Max, 2026-10-12: "cho tui tính năng search project hoặc là SKU
   nhé", over a dropdown of thirty-odd projects. The box matches the project (id or name) AND the SKU
   (id or item name), so "9036", "Musinsa" and "Back Pack" all find their way; a project that matches
   shows every approved SKU in it, a SKU that matches shows under its project. Ticks are kept across
   searches and across projects, so SKUs from two projects can be picked in one go — moveSkusInto
   already sends one request per source. */
const norm = (v) => String(v || '').toLowerCase();

export function MoveSkusPicker({ api, teamKey, excludeGroupId, taken = [], onAdd, onClose }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(() => new Map());   // projectId -> { sku, group }

  useEffect(() => {
    let dead = false;
    const qs = new URLSearchParams({ team: teamKey || '', exclude: excludeGroupId || '' }).toString();
    api(`/move-sources?${qs}`)
      .then((d) => { if (!dead) setGroups(d.groups || []); })
      .catch((e) => { if (!dead) { setGroups([]); setErr(e.message); } });
    return () => { dead = true; };
  }, [api, teamKey, excludeGroupId]);

  const takenIds = useMemo(() => new Set(taken.map((x) => x.projectId)), [taken]);
  const shown = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    const hit = (text) => words.every((w) => text.includes(w));
    return (groups || []).map((g) => {
      const skus = g.skus.filter((sku) => !takenIds.has(sku.projectId));
      if (!words.length || hit(norm(`${g.groupId} ${g.name}`))) return { ...g, skus };
      return { ...g, skus: skus.filter((sku) => hit(norm(`${sku.projectId} ${sku.itemName}`))) };
    }).filter((g) => g.skus.length);
  }, [groups, q, takenIds]);

  const toggle = (g, sku) => setPicked((prev) => {
    const next = new Map(prev);
    if (next.has(sku.projectId)) next.delete(sku.projectId); else next.set(sku.projectId, { sku, group: g });
    return next;
  });
  const toggleGroup = (g) => setPicked((prev) => {
    const next = new Map(prev);
    const all = g.skus.every((sku) => next.has(sku.projectId));
    g.skus.forEach((sku) => { if (all) next.delete(sku.projectId); else next.set(sku.projectId, { sku, group: g }); });
    return next;
  });
  const day = (v) => (v ? String(v).slice(0, 10) : '');

  return (
    <div className="st-modal-overlay st-overlay-top" role="dialog" aria-modal="true">
      <div className="st-modal mv-picker">
        <div className="st-modal-head">
          <h3>{t('st.mvHead')}</h3>
          <button type="button" className="st-modal-close" aria-label={t('xMove.close')} onClick={onClose}>&times;</button>
        </div>
        <div className="st-modal-body">
          <p className="pt-help">{t('st.mvIntro')}</p>
          {!!err && <div className="st-alert error">{err}</div>}
          <label className="mv-field">
            <span>{t('st.mvSearch')}</span>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input
              type="search"
              className="mv-search"
              autoFocus
              value={q}
              placeholder={t('st.mvSearchPh')}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          {groups === null && <p className="pt-help">{t('st.mvLoading')}</p>}
          {groups && !groups.length && !err && <p className="pt-help">{t('st.mvNone')}</p>}
          {groups && !!groups.length && !shown.length && <p className="pt-help">{t('st.mvNoMatch')}</p>}
          {!!shown.length && (
            <div className="mv-results">
              <table className="mv-table">
                <thead>
                  <tr>
                    <th aria-label={t('xMove.pickCol')} />
                    <th>SKU</th>
                    <th>{t('st.mvColItem')}</th>
                    <th>{t('st.mvColApproved')}</th>
                    <th>{t('st.mvColDeveloper')}</th>
                    <th>{t('st.mvColDesigner')}</th>
                  </tr>
                </thead>
                {shown.map((g) => {
                  const all = g.skus.every((sku) => picked.has(sku.projectId));
                  return (
                    <tbody key={g.groupId}>
                      <tr className="mv-grouprow">
                        <td>
                          <input type="checkbox" aria-label={g.groupId} checked={all} onChange={() => toggleGroup(g)} />
                        </td>
                        <td colSpan={5}><strong className="mv-gid">{g.groupId}</strong> {g.name}</td>
                      </tr>
                      {g.skus.map((sku) => (
                        <tr key={sku.projectId} className="mv-skurow">
                          <td>
                            <input type="checkbox" aria-label={sku.projectId} checked={picked.has(sku.projectId)} onChange={() => toggle(g, sku)} />
                          </td>
                          <td className="mv-id">{sku.projectId}</td>
                          <td>{sku.itemName}</td>
                          <td>{[sku.approvedBy, day(sku.approvedAt)].filter(Boolean).join(' · ')}</td>
                          <td>{sku.developer || '—'}</td>
                          <td>{sku.designer || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  );
                })}
              </table>
            </div>
          )}
        </div>
        <div className="st-modal-foot st-form-actions">
          {picked.size > 0 && <span className="mv-picked">{t('st.mvPicked', { n: picked.size })}</span>}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('st.mvCancel')}</button>
          <button
            type="button"
            className="btn btn-primary btn-sm mv-add"
            disabled={!picked.size}
            onClick={() => onAdd([...picked.values()].map(({ sku, group }) => ({
              ...sku, fromGroupId: group.groupId, fromGroupName: group.name, teamKey,
            })))}
          >
            {t('st.mvAdd', { n: picked.size })}
          </button>
        </div>
      </div>
    </div>
  );
}

/* What will move in when the form is saved. Nothing has moved yet, and the line says so. */
export function MoveInList({ items, teamKey, onRemove }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  const strayTeam = items.some((x) => x.teamKey !== teamKey);
  return (
    <div className="mv-list">
      <div className="mv-list-head">{t('st.mvListHead', { n: items.length })}</div>
      {strayTeam && <div className="st-alert error">{t('st.mvTeamChanged')}</div>}
      {items.map((x) => (
        <div className="mv-item" key={x.projectId}>
          <span className="pt-mini-chip">{x.projectId}</span>
          <span className="mv-item-name">{x.itemName}</span>
          <span className="pt-mini-chip mv-from">{t('st.mvFrom', { id: x.fromGroupId })}</span>
          <span className="pt-mini-chip">{x.approvedBy ? t('st.mvApprovedBy', { who: x.approvedBy }) : ''}</span>
          <button type="button" className="pt-sku-del" title={t('st.mvRemove')} onClick={() => onRemove(x.projectId)}>&times;</button>
        </div>
      ))}
      <div className="pt-help">{t('st.mvSaveHint')}</div>
    </div>
  );
}

/* Move them, once the form's own save has landed. One request per source project. Returns what
   happened rather than throwing: the project was saved either way, and the caller says which SKUs
   did not move and why. */
export async function moveSkusInto(api, targetGroupId, items) {
  const bySource = new Map();
  items.forEach((x) => bySource.set(x.fromGroupId, [...(bySource.get(x.fromGroupId) || []), x.projectId]));
  let moved = 0;
  const failed = [];
  for (const [from, skuIds] of bySource) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const d = await api(`/groups/${encodeURIComponent(from)}/move-skus`, {
        method: 'POST', body: JSON.stringify({ skuIds, targetGroupId }),
      });
      moved += d.moved || 0;
    } catch (e) { failed.push(`${from}: ${e.message}`); }
  }
  return { moved, failed };
}
